import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Phone, Mail, MapPin } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  const s = useSiteSettings();
  return (
    <footer className="bg-nj-dark text-white/80 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-12 md:grid-cols-4 lg:grid-cols-5">
        <div className="md:col-span-1">
          <div className="font-serif text-2xl text-white">NJ Conciergerie</div>
          <div className="text-xs tracking-[0.28em] uppercase text-nj-gold mt-1">Rouen</div>
          <p className="text-sm mt-5 leading-relaxed text-white/60">{t("footer.tagline")}</p>
          <a href="https://www.visiterouen.com/offres/la-conciergerie-rouen-fr-5968811/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-nj-gold mt-4 hover:underline">Partenaire Office de Tourisme de Rouen</a>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Conciergerie à Rouen</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/location-courte-duree-rouen" className="hover:text-nj-gold transition-colors">Location courte durée Rouen</Link></li>
            <li><Link to="/conciergerie-airbnb-rouen" className="hover:text-nj-gold transition-colors">Conciergerie Airbnb Rouen</Link></li>
            <li><Link to="/gestion-location-courte-duree-rouen" className="hover:text-nj-gold transition-colors">Gestion location courte durée</Link></li>
            <li><Link to="/conciergerie-booking-rouen" className="hover:text-nj-gold transition-colors">Conciergerie Booking Rouen</Link></li>
            <li><Link to="/menage-airbnb-rouen" className="hover:text-nj-gold transition-colors">Ménage Airbnb Rouen</Link></li>
            <li><Link to="/estimation-revenus-airbnb-rouen" className="hover:text-nj-gold transition-colors">Estimer mes revenus</Link></li>
            <li><Link to="/tarifs-conciergerie-rouen" className="hover:text-nj-gold transition-colors">Tarifs conciergerie</Link></li>
            <li><Link to="/combien-coute-conciergerie-airbnb-rouen" className="hover:text-nj-gold transition-colors">Combien coûte une conciergerie ?</Link></li>
            <li><Link to="/comment-fonctionne-notre-conciergerie" className="hover:text-nj-gold transition-colors">Comment ça marche</Link></li>
            <li><Link to="/pourquoi-nj-conciergerie" className="hover:text-nj-gold transition-colors">Pourquoi NJ Conciergerie</Link></li>
            <li><Link to="/agence-conciergerie-rouen" className="hover:text-nj-gold transition-colors">Notre agence à Rouen</Link></li>
            <li><Link to="/conciergerie-rouen-rive-droite" className="hover:text-nj-gold transition-colors">Rouen Rive Droite</Link></li>
            <li><Link to="/conciergerie-rouen-rive-gauche" className="hover:text-nj-gold transition-colors">Rouen Rive Gauche</Link></li>
            <li><Link to="/conciergerie-bois-guillaume" className="hover:text-nj-gold transition-colors">Bois-Guillaume</Link></li>
            <li><Link to="/conciergerie-mont-saint-aignan" className="hover:text-nj-gold transition-colors">Mont-Saint-Aignan</Link></li>
            <li><Link to="/conciergerie-rouen-centre" className="hover:text-nj-gold transition-colors">Rouen Centre</Link></li>
            <li><Link to="/conciergerie-rouen-gare" className="hover:text-nj-gold transition-colors">Rouen Gare</Link></li>
            <li><Link to="/reglementation-airbnb-rouen" className="hover:text-nj-gold transition-colors">Réglementation Airbnb Rouen</Link></li>
            <li><Link to="/resultats-proprietaires" className="hover:text-nj-gold transition-colors">Résultats propriétaires</Link></li>
            <li><Link to="/taxe-de-sejour-rouen" className="hover:text-nj-gold transition-colors">Taxe de séjour Rouen</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">{t("footer.quickLinks")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/logements" className="hover:text-nj-gold transition-colors">{t("nav.properties")}</Link></li>
            <li><Link to="/conciergerie" className="hover:text-nj-gold transition-colors">{t("nav.concierge")}</Link></li>
            <li><Link to="/confier-mon-logement" className="hover:text-nj-gold transition-colors">{t("nav.entrust")}</Link></li>
            <li><Link to="/decouvrir-rouen" className="hover:text-nj-gold transition-colors">{t("nav.blog")}</Link></li>
            <li><Link to="/consigne" className="hover:text-nj-gold transition-colors">Consigne à bagages</Link></li>
            <li><Link to="/a-propos" className="hover:text-nj-gold transition-colors">{t("nav.about")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">{t("footer.contactUs")}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-nj-gold" /><a href={`tel:${s.phone_intl}`} className="hover:text-nj-gold">{s.phone}</a></li>
            <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-nj-gold" /><a href={`mailto:${s.email}`} className="hover:text-nj-gold">{s.email}</a></li>
            <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-nj-gold mt-0.5" /><span>{s.address}</span></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">{t("footer.legal")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/mentions-legales" className="hover:text-nj-gold">{t("footer.mentions")}</Link></li>
            <li><Link to="/confidentialite" className="hover:text-nj-gold">{t("footer.privacy")}</Link></li>
            <li><Link to="/cgv" className="hover:text-nj-gold">{t("footer.cgv")}</Link></li>
            <li><Link to="/cookies" className="hover:text-nj-gold">{t("footer.cookies")}</Link></li>
            <li>
              <button
                type="button"
                data-testid="manage-cookies-btn"
                onClick={() => {
                  try { localStorage.removeItem("nj_cookies"); } catch (e) { /* ignore */ }
                  window.dispatchEvent(new Event("nj-cookies-manage"));
                }}
                className="hover:text-nj-gold text-left"
              >
                Gérer mes cookies
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-white/50 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} NJ Conciergerie. {t("footer.rights")}</span>
          <span>laconciergerierouen.com</span>
        </div>
      </div>
    </footer>
  );
}
